import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const PublicHeader = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const NAV = [
    { label: t("nav.how_it_works"), to: "/come-funziona" },
    { label: t("nav.solutions"), to: "/soluzioni" },
    { label: "Manager", to: "/manager-network" },
    { label: "Pricing", to: "/pricing" },
    { label: t("nav.for_companies"), to: "/per-le-imprese" },
    { label: t("nav.for_managers"), to: "/per-i-manager" },
    { label: t("nav.faq"), to: "/faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const dashboardLink = user ? (user.role === "admin" ? "/admin" : user.role === "manager" ? "/manager" : "/app") : "/login";

  return (
    <header className={`fixed top-0 inset-x-0 z-40 transition-colors duration-200 ${scrolled ? "bg-white/90 backdrop-blur border-b border-slate-200" : "bg-transparent"}`} data-testid="public-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Logo size="md" />

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="text-sm font-medium text-slate-700 hover:text-[#2D6BFF] transition-colors">
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher />
            {user ? (
              <Link to={dashboardLink}>
                <Button variant="ghost" data-testid="header-dashboard-btn">{t("nav.go_to_dashboard")}</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="ghost" data-testid="header-login-btn">{t("common.login")}</Button>
              </Link>
            )}
            <Link to="/checkup">
              <Button className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white" data-testid="header-checkup-btn">
                {t("nav.free_checkup")}
              </Button>
            </Link>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="header-mobile-menu-btn">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-96 bg-white">
              <div className="py-6">
                <div className="flex items-center justify-between"><Logo size="md" /><LanguageSwitcher /></div>
                <ul className="mt-8 space-y-1">
                  {NAV.map((n) => (
                    <li key={n.to}>
                      <Link to={n.to} className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-slate-50 text-base font-medium text-slate-900">
                        {n.label}
                        <ChevronRight size={16} className="text-slate-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 grid gap-2">
                  <Link to="/checkup">
                    <Button className="w-full bg-[#2D6BFF] hover:bg-[#1F57F2] text-white" data-testid="mobile-checkup-btn">{t("nav.free_checkup")}</Button>
                  </Link>
                  {user ? (
                    <Link to={dashboardLink}>
                      <Button variant="outline" className="w-full">{t("nav.go_to_dashboard")}</Button>
                    </Link>
                  ) : (
                    <Link to="/login">
                      <Button variant="outline" className="w-full">{t("common.login")}</Button>
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
